import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretUp, faCaretDown } from "@fortawesome/free-solid-svg-icons";

import { useAppData } from "../context/AppDataContext";
import { errorToast, successfulToast } from "../util/toastNotifications";

const AddToSprint = ({ selectedTasks, setSelectedTasks, resetSelection }) => {
  const { api } = useAppData();
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [sprintMenuItems, setSprintMenuItems] = useState(null);
  const [selectedSprint, setSelectedSprint] = useState({});
  const [activeSprints, setActiveSprints] = useState([]);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        !triggerRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const timeoutId = window.setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleActiveSprints = async () => {
      try {
        const allActiveSprints = await api.sprint.getActiveSprints();
        setActiveSprints(allActiveSprints);
      } catch (error) {
        console.error("Failed to fetch active sprints", error);
      }
    };
    handleActiveSprints();
  }, []);

  const renderSprintsToSelect = () => {
    return activeSprints.map((sprint) => (
      <div
        key={sprint.sprint_id}
        value={sprint.sprint_id}
        className="sprint-option"
        onClick={() => {
          setSelectedSprint(sprint);
          setIsOpen(false);
        }}
      >
        {sprint.name}
      </div>
    ));
  };

  const handleAddTasksToSprint = async () => {
    if (!selectedSprint || selectedTasks.length === 0) return;
    try {
      const targetSprintId = selectedSprint.sprint_id;
      await api.sprint.assignTasks(targetSprintId, selectedTasks);
      setSelectedTasks([]);
      setSelectedSprint({});
      resetSelection();
      successfulToast("Task added to sprint");
    } catch (error) {
      console.error("Failed to add task to sprint", error);
      errorToast("Failed to add to sprint");
    }
  };

  const handleToggleDropdown = () => {
    setIsOpen((prev) => {
      const nextIsOpen = !prev;
      if (nextIsOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
      return nextIsOpen;
    });
  };

  return (
    <div className="add-to-sprint-container">
      <div className="sprints-selection-wrapper">
        <div className="sprint-dropdown-wrapper" ref={triggerRef}>
          <div
            className="dropdown-button"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={handleToggleDropdown}
            style={{
              cursor: isOpen ? "default" : "pointer",
            }}
          >
            <div className="dropdown-title">
              {Object.keys(selectedSprint).includes("name")
                ? selectedSprint.name
                : "Add Tasks to Sprint"}
            </div>

            <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretUp} />
          </div>

          {isOpen &&
            createPortal(
              <div
                ref={menuRef}
                className="dropdown-options-wrapper dropdown-options-portal"
                style={{
                  top: menuPosition.top,
                  left: menuPosition.left,
                  width: menuPosition.width,
                }}
              >
                {activeSprints.length === 0 ? (
                  <div className="sprint-option" disabled>
                    No Results
                  </div>
                ) : (
                  renderSprintsToSelect()
                )}
              </div>,
              document.body
            )}
        </div>

        <button
          className="add-tasks-button"
          onClick={handleAddTasksToSprint}
          disabled={!selectedSprint?.sprint_id || selectedTasks.length === 0}
        >
          Add to Sprint
        </button>
      </div>
    </div>
  );
};

export default AddToSprint;
